export const THUMBNAIL_MAX_EDGE = 128;

const RASTER_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

/**
 * Whether a content type can have a thumbnail at all. Grids use this to decide whether a
 * missing thumbnail is expected (per-type icon) or worth fetching.
 */
export function isThumbnailable(contentType: string | null | undefined): boolean {
  return !!contentType && (RASTER_TYPES.has(contentType) || contentType === "application/pdf");
}

/**
 * Rasterises a supported file to a small WebP for upload alongside the original.
 * Returns null for unsupported types and on ANY failure — a thumbnail is decoration
 * and must never break an upload. Aspect-fit, never cropped: CSS object-cover at the
 * render site decides framing, so a larger slot later needs no regeneration.
 */
export async function generateThumbnail(file: File): Promise<Blob | null> {
  try {
    if (RASTER_TYPES.has(file.type)) return await fromRaster(file);
    if (file.type === "application/pdf") {
      // pdf.js is heavy; the dynamic import means it costs nothing until a PDF is picked.
      const { renderPdfFirstPage } = await import("./thumbnail-pdf");
      const canvas = await renderPdfFirstPage(file, THUMBNAIL_MAX_EDGE);
      return canvas ? await toWebP(canvas) : null;
    }
    return null;
  } catch {
    return null;
  }
}

async function fromRaster(file: File): Promise<Blob | null> {
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = fit(bitmap.width, bitmap.height, THUMBNAIL_MAX_EDGE);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, width, height);
    return await toWebP(canvas);
  } finally {
    bitmap.close();
  }
}

function fit(w: number, h: number, maxEdge: number): { width: number; height: number } {
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h };
  const scale = maxEdge / Math.max(w, h);
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

function toWebP(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (webp) => {
        if (webp) return resolve(webp);
        // Some engines have no webp encoder; the server accepts png too.
        canvas.toBlob((png) => resolve(png), "image/png");
      },
      "image/webp",
      0.8,
    );
  });
}
